import { useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Trash2, FileText, Loader2, CheckCircle, XCircle, Send, RotateCcw, Copy, AlertCircle, FileDown } from 'lucide-react';
import { propostaService, Proposta } from '../../../services/propostaService';
import api from '../../../services/api';
import { PropostaForm } from './PropostaForm';
import { PropostaPreview } from './PropostaPreview';
import { PublicarPortalButton } from '../../../components/PublicarPortalButton';
import { formatDateFromDB } from '../../../utils/dateUtils';

const statusColors: Record<string, string> = {
  rascunho: 'bg-muted text-foreground',
  enviada: 'bg-blue-100 text-blue-700',
  aprovada: 'bg-green-100 text-green-700',
  rejeitada: 'bg-red-100 text-red-700',
  expirada: 'bg-orange-100 text-orange-700',
};

const statusLabels: Record<string, string> = {
  rascunho: 'Rascunho',
  enviada: 'Enviada',
  aprovada: 'Aprovada',
  rejeitada: 'Rejeitada',
  expirada: 'Expirada',
};

// Proposta enviada cuja data de validade já passou
function isPropostaExpirada(proposta: Proposta): boolean {
  if (proposta.status !== 'enviada') return false;
  return new Date(proposta.dataValidade) < new Date();
}

interface PropostaManagementProps {
  onNavigateToClients?: () => void;
}

export function PropostaManagement({ onNavigateToClients }: PropostaManagementProps = {}) {
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState<'list' | 'form' | 'preview'>('list');
  const [selectedProposta, setSelectedProposta] = useState<Proposta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState('');
  const [clients, setClients] = useState<{ id: string; razaoSocial: string; nomeFantasia?: string }[]>([]);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (view === 'list') loadPropostas();
  }, [view, statusFilter, clientFilter, currentPage]);

  const loadPropostas = async () => {
    setIsLoading(true);
    try {
      const result = await propostaService.list({ clientId: clientFilter || undefined, status: statusFilter || undefined, page: currentPage, limit: 15 });
      setPropostas(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch (error) {
      console.error('Erro ao carregar propostas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await api.get('/admin/clients?limit=200');
      const data = response.data?.data || response.data;
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta proposta?')) return;
    try {
      await propostaService.delete(id);
      loadPropostas();
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Erro ao excluir proposta';
      alert(msg);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    const labels: Record<string, string> = { enviada: 'enviar', aprovada: 'aprovar', rejeitada: 'rejeitar', rascunho: 'reabrir' };
    const action = labels[status] || status;
    let motivo: string | undefined;
    if (status === 'rejeitada') {
      const input = window.prompt('Motivo da rejeição (opcional \u2013 visível no histórico):');
      if (input === null) return; // usuário cancelou
      motivo = input.trim() || undefined;
    } else {
      if (!confirm(`Tem certeza que deseja ${action} esta proposta?`)) return;
    }
    try {
      await propostaService.updateStatus(id, status, motivo);
      loadPropostas();
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Erro ao atualizar status';
      alert(msg);
    }
  };

  const handleEnviar = (id: string) => handleUpdateStatus(id, 'enviada');
  const handleReabrir = (id: string) => handleUpdateStatus(id, 'rascunho');

  const handleDuplicate = async (proposta: Proposta) => {
    if (!confirm(`Duplicar proposta #${proposta.numero}? Uma cópia será criada como Rascunho.`)) return;
    setDuplicating(proposta.id);
    try {
      await propostaService.duplicate(proposta.id);
      loadPropostas();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao duplicar proposta');
    } finally {
      setDuplicating(null);
    }
  };

  const handleEdit = async (proposta: Proposta) => {
    const full = await propostaService.getById(proposta.id);
    setSelectedProposta(full);
    setView('form');
  };

  const handlePreview = async (proposta: Proposta) => {
    const full = await propostaService.getById(proposta.id);
    setSelectedProposta(full);
    setView('preview');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const filteredPropostas = propostas.filter(p =>
    p.client.razaoSocial.toLowerCase().includes(searchTerm.replace('#', '').toLowerCase()) ||
    (p.client.nomeFantasia || '').toLowerCase().includes(searchTerm.replace('#', '').toLowerCase()) ||
    p.numero.toString().includes(searchTerm.replace('#', ''))
  );

  // Fluxo direto para o Formulário (nova proposta)
  if (view === 'form') {
    return (
      <PropostaForm
        proposta={selectedProposta}
        onBack={() => {
          setView('list');
          setSelectedProposta(null);
        }}
        onSaved={() => { setView('list'); setSelectedProposta(null); loadPropostas(); }}
        onAddClient={onNavigateToClients}
      />
    );
  }

  if (view === 'preview' && selectedProposta) {
    return (
      <PropostaPreview
        proposta={selectedProposta}
        onBack={() => { setView('list'); setSelectedProposta(null); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2">
          <button onClick={() => { setSelectedProposta(null); setView('form'); }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white rounded-[8px] shadow-[0_4px_12px_-2px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_18px_-4px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 transition-all duration-200">
            <Plus className="w-4 h-4" /> Nova Proposta
          </button>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-input rounded-lg w-64 text-foreground bg-background" />
          </div>
          <select value={clientFilter} onChange={e => { setClientFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-input rounded-lg text-foreground bg-background">
            <option value="">Todos os clientes</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.nomeFantasia || c.razaoSocial}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-input rounded-lg text-foreground bg-background">
            <option value="">Todos os status</option>
            <option value="rascunho">Rascunho</option>
            <option value="enviada">Enviada</option>
            <option value="aprovada">Aprovada</option>
            <option value="rejeitada">Rejeitada</option>
          </select>
        </div>
      </div>


      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-brand animate-spin" />
          </div>
        ) : filteredPropostas.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">Nenhuma proposta encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Nº</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Valor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Data</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPropostas.map(proposta => (
                <tr key={proposta.id} className="hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium text-foreground">#{proposta.numero}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground leading-snug">
                      {proposta.client.nomeFantasia || proposta.client.razaoSocial}
                    </div>
                    {proposta.titulo && (
                      <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[220px]" title={proposta.titulo}>
                        {proposta.titulo}
                      </div>
                    )}
                    {proposta.status === 'rejeitada' && proposta.motivoRejeicao && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-0.5 truncate max-w-[220px]" title={proposta.motivoRejeicao}>
                        ↓ {proposta.motivoRejeicao}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{proposta.tipo === 'vendas' ? 'Produtos' : 'Serviços'}</td>
                  <td className="px-4 py-3 font-medium text-foreground">
                    {proposta.valorGlobal != null
                      ? formatCurrency(Number(proposta.valorGlobal))
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const expirada = isPropostaExpirada(proposta);
                      const key = expirada ? 'expirada' : proposta.status;
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[key]}`}>
                          {expirada && <AlertCircle className="w-3 h-3" />}
                          {statusLabels[key]}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDateFromDB(proposta.dataEmissao)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handlePreview(proposta)} className="p-2 hover:bg-muted rounded" title="Visualizar">
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </button>
                      {/* Editar: apenas rascunho ou enviada */}
                      {(proposta.status === 'rascunho' || proposta.status === 'enviada') && (
                        <button onClick={() => handleEdit(proposta)} className="p-2 hover:bg-muted rounded" title="Editar">
                          <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </button>
                      )}
                      {/* Enviar: apenas rascunho */}
                      {proposta.status === 'rascunho' && (
                        <button onClick={() => handleEnviar(proposta.id)} className="p-2 hover:bg-muted rounded" title="Enviar para cliente">
                          <Send className="w-4 h-4 text-blue-500" />
                        </button>
                      )}
                      {/* Aprovar/Rejeitar: apenas enviada */}
                      {proposta.status === 'enviada' && (
                        <>
                          <button onClick={() => handleUpdateStatus(proposta.id, 'aprovada')} className="p-2 hover:bg-muted rounded" title="Aprovar">
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </button>
                          <button onClick={() => handleUpdateStatus(proposta.id, 'rejeitada')} className="p-2 hover:bg-muted rounded" title="Rejeitar">
                            <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </button>
                        </>
                      )}
                      {/* Reabrir: apenas rejeitada */}
                      {proposta.status === 'rejeitada' && (
                        <button onClick={() => handleReabrir(proposta.id)} className="p-2 hover:bg-muted rounded" title="Reabrir (voltar p/ rascunho)">
                          <RotateCcw className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        </button>
                      )}
                      {/* Excluir: apenas rascunho */}
                      {proposta.status === 'rascunho' && (
                        <button onClick={() => handleDelete(proposta.id)} className="p-2 hover:bg-muted rounded" title="Excluir">
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      )}
                      {/* Duplicar: sempre disponível */}
                      <button
                        onClick={() => handleDuplicate(proposta)}
                        disabled={duplicating === proposta.id}
                        className="p-2 hover:bg-muted rounded"
                        title="Duplicar como novo Rascunho"
                      >
                        {duplicating === proposta.id
                          ? <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
                          : <Copy className="w-4 h-4 text-muted-foreground" />}
                      </button>
                      {/* Gerar PDF: sempre disponível */}
                      <button
                        type="button"
                        onClick={async () => {
                          try { await propostaService.downloadPdf(proposta.id, proposta.numero); }
                          catch { alert('Erro ao gerar PDF'); }
                        }}
                        className="p-2 hover:bg-muted rounded"
                        title="Gerar PDF"
                      >
                        <FileDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      </button>
                      {/* Publicar no Portal */}
                      <PublicarPortalButton
                        tipo="proposta"
                        documentoId={proposta.id}
                        variant="icon"
                        publicado={proposta.publicadoPortal}
                        onSuccess={loadPropostas}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-border">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1 border border-input rounded hover:bg-muted disabled:opacity-50 text-foreground">Anterior</button>
            <span className="text-sm text-muted-foreground">Página {currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1 border border-input rounded hover:bg-muted disabled:opacity-50 text-foreground">Próxima</button>
          </div>
        )}
      </div>
    </div>
  );
}
